import { useQuery } from '@tanstack/react-query';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import patientService from '../../services/patientService';
import { parsePatientId } from './usePatientBasicInfo';
import api from '../../services/api';

export interface PatientPrescriptionsData {
  medications: any[];
  metrics: {
    adherenceRate: number;
    daysUntilNextRefill: number;
    pendingRefills: number;
    overdueItems: number;
  };
}

/**
 * Calculate adherence rate from medications
 */
function calculateAdherenceRate(medications: any[]): number {
  if (!medications || medications.length === 0) return 100;

  const adherentMeds = medications.filter((m: any) => {
    const daysSupply = m.pharmacistVerifiedDaysSupply || m.DaysSupply || m.daysSupply;
    const lastFill = m.LastFillDate || m.lastFillDate;
    if (!lastFill || !daysSupply) return true;
    const lastFillDate = new Date(lastFill);
    const expectedRefill = new Date(lastFillDate.getTime() + (daysSupply * 24 * 60 * 60 * 1000));
    return expectedRefill > new Date();
  });

  return Math.round((adherentMeds.length / medications.length) * 100);
}

/**
 * Hook to fetch patient prescriptions/medications
 * Only fetches when Prescriptions tab is active
 */
export function usePatientPrescriptions(
  patientId: string | undefined,
  options?: { enabled?: boolean }
) {
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  const { pharmacyId, patientNumber } = parsePatientId(patientId, activePharmacy?.id);

  const enabled = options?.enabled !== false && !!pharmacyId && !!patientNumber;

  return useQuery({
    queryKey: ['patient', 'prescriptions', pharmacyId, patientNumber],
    queryFn: async (): Promise<PatientPrescriptionsData> => {
      if (!pharmacyId || !patientNumber) {
        throw new Error('Invalid patient ID');
      }

      const response = await patientService.getRawPatientPrescriptions(
        pharmacyId,
        patientNumber,
        'all',
        100
      );

      const medications = response?.data?.results || [];

      // Calculate metrics
      const activeMeds = medications.filter((m: any) =>
        m.RxStatus === 1 || m.status === 1
      );

      const nextRefillDates = activeMeds
        .map((m: any) => m.NextFillDate || m.nextFillDate)
        .filter(Boolean)
        .sort();

      const daysUntilNextRefill = nextRefillDates.length > 0
        ? Math.ceil((new Date(nextRefillDates[0]).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : -1;

      return {
        medications,
        metrics: {
          adherenceRate: calculateAdherenceRate(activeMeds),
          daysUntilNextRefill,
          pendingRefills: activeMeds.filter((m: any) =>
            (m.RefillsRemaining || m.refillsRemaining || 0) > 0
          ).length,
          overdueItems: activeMeds.filter((m: any) => {
            const nextFill = m.NextFillDate || m.nextFillDate;
            if (!nextFill) return false;
            return new Date(nextFill) < new Date();
          }).length,
        }
      };
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ---------------------------------------------------------------------------
// usePatientAllPrescriptions — wraps the all-prescriptions Kroll endpoint.
// Same query AllPrescriptions used inline; extracted so the Overview tab can
// reuse it (one source of truth). Keyed by (pharmacyId, patientNumber, filter).
// ---------------------------------------------------------------------------

export type RxStatusFilter = 'active' | 'inactive' | 'all';

/**
 * Patient Kroll prescriptions (rows carry RxNum, FillDate, DaysSupply, FollowUpDate…).
 * Keyed by ['patient', 'all-prescriptions', pharmacyId, patientNumber, filter].
 */
export function usePatientAllPrescriptions(patientId: string | undefined, filter: RxStatusFilter = 'all') {
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  // Use the shared composite-id parser (same as the sibling patient hooks) so
  // there's one source of truth for id parsing and the fallbackPharmacyId path.
  const { pharmacyId, patientNumber } = parsePatientId(patientId, activePharmacy?.id);

  return useQuery({
    queryKey: ['patient', 'all-prescriptions', pharmacyId, patientNumber, filter],
    queryFn: async () => {
      const response = await api.get(`/api/rx/${pharmacyId}/patient/${patientNumber}/all-prescriptions`, {
        params: { filter },
      });
      if (response.data?.success) return response.data.data || [];
      throw new Error('Failed to fetch prescriptions');
    },
    enabled: !!pharmacyId && !!patientNumber,
    staleTime: 2 * 60 * 1000,
  });
}

/** Map of rxNumber (string) → most-recent FillDate (YYYY-MM-DD) for quick anchor lookup. */
export function buildRxFillDateMap(prescriptions: any[]): Map<string, string> {
  const map = new Map<string, string>();
  (prescriptions || []).forEach((rx: any) => {
    if (rx?.RxNum == null || !rx?.FillDate) return;
    const key = String(rx.RxNum);
    const fill = String(rx.FillDate).split('T')[0]; // YYYY-MM-DD, no UTC shift
    const existing = map.get(key);
    if (!existing || existing < fill) map.set(key, fill);
  });
  return map;
}
